import { localizeAssetTypeLabel } from './assetTypeLabels';
import { localizeGeoLabel } from './israelGeoLabels';
import type { TranslateFn } from './operationLabels';
import type { AppLanguage } from './types';

function normalizeKey(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[''`]/g, '')
    .replace(/[/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[_-]+/g, ' ');
}

function lookupHebrew(map: Record<string, string>, label: string): string | undefined {
  const raw = label.trim();
  if (!raw) {
    return undefined;
  }
  if (/[\u0590-\u05FF]/.test(raw)) {
    return raw;
  }
  const key = normalizeKey(raw);
  if (map[key] !== undefined) {
    return map[key];
  }
  const lower = raw.toLowerCase();
  if (map[lower] !== undefined) {
    return map[lower];
  }
  const simplified = key.replace(/[.,]/g, '').trim();
  if (simplified !== key && map[simplified] !== undefined) {
    return map[simplified];
  }
  const suffixes = [
    ' for the room',
    ' for the space',
    ' with hdmi',
    ' services',
    ' service',
    ' equipment',
    ' facilities',
  ];
  for (const suffix of suffixes) {
    if (key.endsWith(suffix)) {
      const shorter = key.slice(0, -suffix.length).trim();
      if (map[shorter] !== undefined) {
        return map[shorter];
      }
    }
  }
  return undefined;
}

/** Common facility names from GET /assets/facilities and list payloads. */
const FACILITY_HE: Record<string, string> = {
  wifi: 'Wi-Fi',
  'wi fi': 'Wi-Fi',
  'wireless internet': 'אינטרנט אלחוטי',
  'high-speed internet': 'אינטרנט מהיר',
  'high speed internet': 'אינטרנט מהיר',
  'high speed wi fi': 'אינטרנט מהיר',
  internet: 'אינטרנט',
  '24/7 access': 'גישה 24/7',
  '24 7 access': 'גישה 24/7',
  'round the clock access': 'גישה 24/7',
  'lounge area': 'אזור לונג',
  lounge: 'לונג',
  bookshelves: 'מדפי ספרים',
  bookshelf: 'מדף ספרים',
  parking: 'חניה',
  'car park': 'חניה',
  'protected space': 'מרחב מוגן',
  'protect space': 'מרחב מוגן',
  'handicap accessible': 'נגיש לנכים',
  'close to bus': 'קרוב לתחבורה ציבורית',
  'close to train': 'קרוב לתחבורה ציבורית',
  'close to bus train': 'קרוב לתחבורה ציבורית',
  'close to bus/train': 'קרוב לתחבורה ציבורית',
  'close to public transportation': 'קרוב לתחבורה ציבורית',
  'public transportation': 'תחבורה ציבורית',
  'near bus': 'קרוב לתחבורה ציבורית',
  'near train': 'קרוב לתחבורה ציבורית',
  'bus stop': 'קרוב לתחבורה ציבורית',
  'train station': 'קרוב לתחבורה ציבורית',
  'free parking': 'חניה חינם',
  'paid parking': 'חניה בתשלום',
  'electric car charging': 'טעינת רכב חשמלי',
  'bicycle storage': 'אחסון אופניים',
  kitchen: 'מטבח',
  projector: 'מקרן',
  'air conditioning': 'מיזוג אוויר',
  ac: 'מיזוג אוויר',
  'a/c': 'מיזוג אוויר',
  whiteboard: 'לוח מחיק',
  coffee: 'קפה',
  'coffee machine': 'מכונת קפה',
  restroom: 'שירותים',
  bathroom: 'שירותים',
  toilet: 'שירותים',
  accessibility: 'נגישות',
  'wheelchair access': 'נגישות לכיסא גלגלים',
  security: 'אבטחה',
  cctv: 'מצלמות אבטחה',
  elevator: 'מעלית',
  lift: 'מעלית',
  printer: 'מדפסת',
  scanner: 'סורק',
  tv: 'טלוויזיה',
  television: 'טלוויזיה',
  balcony: 'מרפסת',
  terrace: 'טרסה',
  garden: 'גינה',
  'meeting room': 'חדר ישיבות',
  'meeting rooms': 'חדרי ישיבות',
  reception: 'קבלה',
  storage: 'אחסון',
  'storage shelves': 'מדפי אחסון',
  'storage shelf': 'מדף אחסון',
  locker: 'לוקר',
  lockers: 'לוקרים',
  'private storage': 'אחסון פרטי',
  'snacks and beverages': 'חטיפים ומשקאות',
  'dedicated it support': 'תמיכה IT ייעודית',
  soundproofing: 'בידוד אקוסטי',
  'shower facilities': 'מתקני מקלחות',
  microwave: 'מיקרוגל',
  fridge: 'מקרר',
  refrigerator: 'מקרר',
  dishwasher: 'מדיח כלים',
  heating: 'חימום',
  'natural light': 'תאורה טבעית',
  'standing desk': 'שולחן עמידה',
  'phone booth': 'תא טלפון',
  'video conferencing': 'ועידת וידאו',
  zoom: 'זום',
  gym: 'חדר כושר',
  'gym access': 'גישה לחדר כושר',
  'fitness room': 'חדר כושר',
  'fitness center': 'מרכז כושר',
  'swimming pool': 'בריכת שחייה',
  pool: 'בריכה',
  sauna: 'סאונה',
  shower: 'מקלחות',
  showers: 'מקלחות',
  'locker room': 'חדר הלבשה',
  'break room': 'חדר מנוחה',
  'waiting area': 'אזור המתנה',
  'conference room': 'חדר כנסים',
  'conference call': 'שיחת ועידה',
  soundproof: 'בידוד אקוסטי',
  'sound proof': 'בידוד אקוסטי',
  'pet friendly': 'ידידותי לחיות מחמד',
  'pet-friendly': 'ידידותי לחיות מחמד',
  'air purifier': 'מטהר אוויר',
  'water cooler': 'בר מים',
  'vending machine': 'מכונת ממכר',
  'on-site maintenance': 'תחזוקה באתר',
  'on site maintenance': 'תחזוקה באתר',
  maintenance: 'תחזוקה',
};

function hebrewCatalogLabel(
  label: string,
  language: AppLanguage,
  kind: CatalogNameKind,
): string {
  if (language !== 'he') {
    return label;
  }
  const localized = localizeCatalogDisplayName(label, language, kind);
  if (localized !== label || /[\u0590-\u05FF]/.test(localized)) {
    return localized;
  }
  return label;
}

/** Add-on catalog names and common patterns. */
const ADDON_HE: Record<string, string> = {
  catering: 'קייטרינג',
  coffee: 'קפה',
  projector: 'מקרן',
  breakfast: 'ארוחת בוקר',
  lunch: 'ארוחת צהריים',
  cleaning: 'ניקיון',
  'extra cleaning': 'ניקיון נוסף',
  equipment: 'ציוד',
  'av equipment': 'ציוד הגברה ותאורה',
  parking: 'חניה',
  'extra parking': 'חניה נוספת',
  security: 'אבטחה',
  'after hours': 'שעות נוספות',
  'overtime': 'שעות נוספות',
  'over time': 'שעות נוספות',
  refreshments: 'כיבוד',
  drinks: 'משקאות',
  snacks: 'חטיפים',
  chairs: 'כיסאות',
  tables: 'שולחנות',
  'extra chairs': 'כיסאות נוספים',
  wifi: 'Wi-Fi',
  whiteboard: 'לוח מחיק',
  'flip chart': 'לוח flipchart',
  flipchart: 'לוח flipchart',
  'childcare services': 'שירותי צהרון',
  'childcare service': 'שירותי צהרון',
  'child care': 'שירותי צהרון',
  'pet-friendly facilities': 'מתקנים ידידותיים לחיות מחמד',
  'pet friendly facilities': 'מתקנים ידידותיים לחיות מחמד',
  'espresso cart': 'עגלת אספרסו',
  markers: 'טושים ללוח',
  'whiteboard markers': 'טושים ללוח',
  'hd projector with hdmi': 'מקרן HDMI',
  'coffee service for the room': 'שירות קפה',
  'coffee service': 'שירות קפה',
  'extra tables': 'שולחנות נוספים',
  'extra table': 'שולחן נוסף',
  'it support': 'תמיכת מחשוב',
  'tech support': 'תמיכה טכנית',
  'printing service': 'שירות הדפסה',
  'late checkout': 'יציאה מאוחרת',
  'early check in': 'כניסה מוקדמת',
  'early check-in': 'כניסה מוקדמת',
  'storage locker': 'לוקר אחסון',
  'meeting room extension': 'הארכת חדר ישיבות',
  'on-site maintenance': 'תחזוקה באתר',
  'on site maintenance': 'תחזוקה באתר',
  maintenance: 'תחזוקה',
  'hd projector': 'מקרן HDMI',
  'sound system': 'מערכת הגברה',
  'pa system': 'מערכת הגברה',
  microphone: 'מיקרופון',
  microphones: 'מיקרופונים',
  speaker: 'רמקול',
  speakers: 'רמקולים',
  stage: 'במה',
  podium: 'דוכן נאומים',
  lectern: 'דוכן נאומים',
  'fruit basket': 'סל פירות',
  'fruit platter': 'מגש פירות',
  'mineral water': 'מים מינרליים',
  'bottled water': 'מים בבקבוקים',
  'tea service': 'שירות תה',
  'flower arrangement': 'סידור פרחים',
  flowers: 'פרחים',
  photography: 'צילום',
  videography: 'צילום וידאו',
  translation: 'תרגום',
  interpreter: 'מתורגמן',
  secretary: 'מזכירות',
  'office supplies': 'ציוד משרדי',
  laptop: 'מחשב נייד',
  'laptop rental': 'השכרת מחשב נייד',
  monitor: 'מסך',
  'extra monitor': 'מסך נוסף',
  screen: 'מסך',
  'hdmi cable': 'כבל HDMI',
  'guest wifi': 'Wi-Fi לאורחים',
  'guest parking': 'חניה לאורחים',
  'valet parking': 'שירות חניה',
  'deep cleaning': 'ניקיון יסודי',
  'cleaning service': 'שירות ניקיון',
  childcare: 'שירותי צהרון',
  'child care services': 'שירותי צהרון',
  'standing tables': 'שולחנות עמידה',
  'standing table': 'שולחן עמידה',
  'extra chairs and tables': 'כיסאות ושולחנות נוספים',
  bartender: 'ברמן',
  'bar service': 'שירות בר',
  waitstaff: 'צוות הגשה',
  waiter: 'מלצר',
  server: 'מגיש',
  sandwiches: 'כריכים',
  salad: 'סלט',
  salads: 'סלטים',
  pastries: 'מאפים',
  dessert: 'קינוח',
  desserts: 'קינוחים',
  'ice cream': 'גלידה',
  pizza: 'פיצה',
  sushi: 'סושי',
  'vegan options': 'אפשרויות טבעוניות',
  'kosher catering': 'קייטרינג כשר',
  signage: 'שילוט',
  banner: 'באנר',
  banners: 'באנרים',
  'welcome kit': 'ערכת קבלת פנים',
  stationery: 'כלי כתיבה',
  notepad: 'פנקס',
  notepads: 'פנקסים',
  pen: 'עט',
  pens: 'עטים',
  'usb drive': 'כונן USB',
  'power bank': 'סוללה ניידת',
  adapter: 'מתאם',
  adapters: 'מתאמים',
  linens: 'מצעים',
  linen: 'מצעים',
  tablecloth: 'מפת שולחן',
  tablecloths: 'מפות שולחן',
  glassware: 'כלי זכוכית',
  cutlery: 'סכו"ם',
  utensils: 'כלי אוכל',
  'ergonomic chair': 'כיסא ארגונומי',
  lockers: 'לוקרים',
  'private storage': 'אחסון פרטי',
  'snacks and beverages': 'חטיפים ומשקאות',
  'dedicated it support': 'תמיכה IT ייעודית',
  'backup power supply': 'ספק כוח גיבוי',
  'smart lighting': 'תאורה חכמה',
  'adjustable thermostat': 'תרמוסטט מתכוונן',
  soundproofing: 'בידוד אקוסטי',
  'dedicated parking spot': 'מקום חניה ייעודי',
  'outdoor seating area': 'אזור ישיבה חיצוני',
  'gaming zone': 'אזור משחקים',
  'library access': 'גישה לספרייה',
  'laundry service': 'שירות כביסה',
  'shower facilities': 'מתקני מקלחות',
  'bike rack': 'מתלה אופניים',
  'charging stations': 'עמדות טעינה',
  '3d printer': 'מדפסת תלת-ממד',
  'vr equipment': 'ציוד VR',
  'green plants/decor': 'צמחים ירוקים',
  'mail handling service': 'שירות טיפול בדואר',
  'package delivery service': 'שירות משלוח חבילות',
  'on-site technician': 'טכנאי באתר',
  'custom branding options': 'אפשרויות מיתוג מותאם אישית',
  'event management support': 'סיוע בניהול אירועים',
  'standing desk': 'שולחן עמידה',
};

/** Pricing type names (GET /assets/pricing-types). */
const PRICING_HE: Record<string, string> = {
  hour: 'לפי שעה',
  hourly: 'לפי שעה',
  'per hour': 'לפי שעה',
  'hourly rate': 'לפי שעה',
  day: 'יומי',
  daily: 'יומי',
  'per day': 'יומי',
  'daily rate': 'יומי',
  week: 'שבועי',
  weekly: 'שבועי',
  'per week': 'שבועי',
  'weekly rate': 'שבועי',
  month: 'חודשי',
  monthly: 'חודשי',
  'per month': 'חודשי',
  'monthly rate': 'חודשי',
  year: 'שנתי',
  yearly: 'שנתי',
  annual: 'שנתי',
  'per year': 'שנתי',
  'yearly rate': 'שנתי',
};

/** asset_status and similar workflow labels. */
const ASSET_STATUS_HE: Record<string, string> = {
  active: 'פעיל',
  inactive: 'לא פעיל',
  pending: 'ממתין',
  draft: 'טיוטה',
  published: 'מפורסם',
  available: 'זמין',
  unavailable: 'לא זמין',
  archived: 'בארכיון',
  disabled: 'מושבת',
  enabled: 'מופעל',
  open: 'פתוח',
  closed: 'סגור',
  approved: 'מאושר',
  rejected: 'נדחה',
};

export type CatalogNameKind = 'facility' | 'addon' | 'pricing' | 'status';

export function localizeCatalogDisplayName(
  name: string,
  language: AppLanguage,
  kind: CatalogNameKind,
): string {
  if (language !== 'he') {
    return name;
  }
  const map =
    kind === 'facility' ? FACILITY_HE : kind === 'addon' ? ADDON_HE : kind === 'pricing' ? PRICING_HE : ASSET_STATUS_HE;
  return lookupHebrew(map, name) ?? name;
}

/** Localize a facility or add-on label from catalog / asset rows. */
export function localizeFacilityOrAddonName(
  name: string,
  language: AppLanguage,
  kind: 'facility' | 'addon',
): string {
  return localizeCatalogDisplayName(name, language, kind);
}

const GEO_VALUE_FIELDS = new Set([
  'country_name',
  'state_name',
  'region_name',
  'district_name',
  'country',
  'state',
  'region',
  'district',
]);

const ASSET_TYPE_VALUE_FIELDS = new Set(['asset_type_name', 'type_name', 'asset_type']);
const ASSET_STATUS_VALUE_FIELDS = new Set(['asset_status']);

const CATALOG_NAME_FIELDS: Record<string, CatalogNameKind> = {
  facility_name: 'facility',
  addon_name: 'addon',
  pricing_type_name: 'pricing',
  type_name: 'pricing',
  name: 'facility',
  title: 'facility',
  label: 'facility',
  facility_type_name: 'facility',
};

function normalizeFieldKey(key: string): string {
  return key.trim().toLowerCase().replace(/\s+/g, '_');
}

/** Localize string values on asset detail rows when the field key is known. */
export function localizeAssetFieldValue(
  fieldKey: string,
  value: unknown,
  language: AppLanguage,
  t: TranslateFn,
): string | null {
  if (language !== 'he') {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const nk = normalizeFieldKey(fieldKey);
    if (GEO_VALUE_FIELDS.has(nk)) {
      return localizeGeoLabel(trimmed, language);
    }
    if (ASSET_TYPE_VALUE_FIELDS.has(nk)) {
      return localizeAssetTypeLabel(trimmed, language, t);
    }
    if (ASSET_STATUS_VALUE_FIELDS.has(nk)) {
      return localizeCatalogDisplayName(trimmed, language, 'status');
    }
    const catalogKind = CATALOG_NAME_FIELDS[nk];
    if (catalogKind) {
      return localizeCatalogDisplayName(trimmed, language, catalogKind);
    }
    return null;
  }
  return null;
}

/** Pick catalog kind from a facilities / add-ons table row. */
export function catalogKindFromRow(row: Record<string, unknown>): CatalogNameKind {
  const typeVal = row.Type ?? row.type;
  if (typeVal === 'Add-on') {
    return 'addon';
  }
  if (typeVal === 'Facility') {
    return 'facility';
  }
  if ('addon_id' in row || 'addon_name' in row) {
    return 'addon';
  }
  return 'facility';
}

/** Localize display title from a catalog row (facilities, add-ons, pricing types). */
export function localizeCatalogRowTitle(
  row: Record<string, unknown>,
  language: AppLanguage,
  t: TranslateFn,
  kind?: CatalogNameKind,
): string {
  const resolvedKind = kind ?? catalogKindFromRow(row);
  const fromName =
    typeof row.pricing_type_name === 'string' && row.pricing_type_name.trim()
      ? row.pricing_type_name.trim()
      : typeof row.addon_name === 'string' && row.addon_name.trim()
        ? row.addon_name.trim()
        : typeof row.facility_name === 'string' && row.facility_name.trim()
          ? row.facility_name.trim()
          : typeof row.type_name === 'string' && row.type_name.trim()
            ? row.type_name.trim()
            : typeof row.name === 'string' && row.name.trim()
              ? row.name.trim()
              : '';
  if (fromName) {
    if (typeof row.asset_type_name === 'string' && row.asset_type_name.trim()) {
      return localizeAssetTypeLabel(fromName, language, t);
    }
    if (resolvedKind === 'pricing') {
      return hebrewCatalogLabel(fromName, language, 'pricing');
    }
    if (resolvedKind === 'addon') {
      const fromNameHe = hebrewCatalogLabel(fromName, language, 'addon');
      if (language !== 'he' || fromNameHe !== fromName || /[\u0590-\u05FF]/.test(fromNameHe)) {
        return fromNameHe;
      }
      const description =
        typeof row.description === 'string' && row.description.trim()
          ? row.description.trim()
          : typeof row.addon_description === 'string' && row.addon_description.trim()
            ? row.addon_description.trim()
            : '';
      if (description) {
        return hebrewCatalogLabel(description, language, 'addon');
      }
      return fromNameHe;
    }
    return hebrewCatalogLabel(fromName, language, resolvedKind);
  }
  if (resolvedKind === 'addon') {
    const description =
      typeof row.description === 'string' && row.description.trim()
        ? row.description.trim()
        : typeof row.addon_description === 'string' && row.addon_description.trim()
          ? row.addon_description.trim()
          : '';
    if (description) {
      return hebrewCatalogLabel(description, language, 'addon');
    }
  }
  const fallback =
    typeof row.asset_name === 'string' && row.asset_name.trim()
      ? row.asset_name.trim()
      : typeof row.id === 'number'
        ? `#${row.id}`
        : '';
  return fallback ? localizeCatalogDisplayName(fallback, language, resolvedKind) : '';
}
